import { DateTimeHelper } from './date-time-helper';

/*
 * The types in this file together implement a simple 'mapper' that, given an object of one type, builds an object of another.
 * The mapping (i.e. which fields are copied, and where to, and with what transformation) is defined using the MapperBuilder
 * type, which is fluent. MapperBuilder then gives you a Mapper, which you can use to actually transform input objects to output
 * objects.
 *
 * It's similar in theory to AutoMapper (blargh), but with many fewer features.
 */
export enum TRANSFORM {
  IDENTITY,
  STRING_TO_DATE
}

export class Mapper {
  constructor(
    private mapping: { [target: string]: any },
  ) {

  }

  public Map(input: any): any {
    if (!input) {
      return null;
    }

    var output = {};

    Object.entries(this.mapping).forEach(
      ([key, rule]) => {
        var value = input[rule.sourceField];

        if (rule.dataTypeTransform == TRANSFORM.STRING_TO_DATE) {
          value = this.getDateFromFilter(value);
        }

        output[key] = value;
      }
    );

    return output;
  }

  protected getDateFromFilter(date): Date {
    return DateTimeHelper.getDateFromDDMMYYYYString(date);
  }
}

export class MapperBuilder {
  private mapping = {};
  private defaultTargetFieldMappingRegExp: RegExp;
  private defaultTargetFieldMappingReplacement: string;

  /**
   * Adds a "copy field" rule to the mapper - the specified sourceField on the input object will be copied to a target field
   * on the output object.
   * @param sourceFieldCode The source field
   * @param dataTypeTransform (Optional) What transformation, if any, to run on the source field's value before writing to the target field
   * @param targetFieldCode (Optional) The target field. If null, then the global 'default target field mapping', if any, is applied, and if none is available, the sourceFieldCode is used.
   * @returns
   */
  public MapFrom(sourceFieldCode: string, dataTypeTransform: TRANSFORM = null, targetFieldCode: string = null): MapperBuilder {
    // If we need to infer the targetFieldCode (because it hasn't been supplied)
    if (!targetFieldCode) {
      // If we've been given a regular expression to use to generate the targetFieldCode from the sourceFieldCode, use it
      if (this.defaultTargetFieldMappingRegExp) {
        targetFieldCode = sourceFieldCode.replace(this.defaultTargetFieldMappingRegExp, this.defaultTargetFieldMappingReplacement);
      } else {
        // Otherwise, just default the targetFieldCode to the sourceFieldCode
        targetFieldCode = sourceFieldCode;
      }
    }

    this.mapping[targetFieldCode] = {
      sourceField: sourceFieldCode,
      dataTypeTransform: dataTypeTransform != null ? dataTypeTransform : TRANSFORM.IDENTITY
    };

    return this;
  }

  /**
   * Adds an automatic 'field rename' rule to the mapper, used when a source field is mapped, but the target field isn't specified.
   * @param regExp
   * @param replacement
   * @returns
   */
  public WithDefaultTargetFieldMapping(regExp: RegExp, replacement: string): MapperBuilder {
    this.defaultTargetFieldMappingRegExp = regExp;
    this.defaultTargetFieldMappingReplacement = replacement;

    return this;
  }

  public BuildMapper() {
    return new Mapper(this.mapping);
  }
  
}

export class MapperHelper {
  public static toBoolOrNull(value: string): boolean | null {
    if (value === '1') return true;
    if (value === '2') return false;
    return null;
  }
}
